import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Button, Container, TextField, Typography } from '@mui/material';
import useSignIn from 'react-auth-kit/hooks/useSignIn';
import Navbar from '../components/Navbar';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { fetchUserLanguage } from '../hooks/UserLanguage';

export default function Login() {
  const { REACT_APP_API_URL } = process.env;
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const signIn = useSignIn();
  const { t } = useTranslation();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!username || !password) {
      setError(t('login.fillFields'));
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${REACT_APP_API_URL}/api/token/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok) {
        signIn({
          auth: {
            token: data.access,
            type: 'Bearer',
          },
        });
        localStorage.setItem('username', username);
        localStorage.setItem('refreshToken', data.refresh);
        const userLang = await fetchUserLanguage(data.access);
        if (userLang) {
          await i18n.changeLanguage(userLang);
        }
        navigate('/');
      } else {
        setError(data.message || t('login.failMessage'));
      }
    } catch (error) {
      const errorMessage = (error as Error).message || t('login.failMessage');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Navbar />
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography id={'login-title'} component="h1" variant="h5">
            {t('login.title')}
          </Typography>
          <Box
            component="form"
            onSubmit={handleLogin}
            noValidate
            sx={{ mt: 1 }}
          >
            <TextField
              margin="normal"
              fullWidth
              label={t('login.username')}
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <TextField
              margin="normal"
              fullWidth
              name="password"
              label={t('login.password')}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && (
              <Typography color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {t('login.login')}
            </Button>
            <Link to="/register" style={{ textDecoration: 'none' }}>
              <Typography variant="body2" color="primary">
                {t('login.registerMessage')}
              </Typography>
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
